import { useCallback, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { SyntheticEvent } from 'react';

import { mediaTokenQueryKey, useMediaToken } from '@/features/auth/useMediaToken';

/**
 * The surface this hook exposes to the consuming `<img>` element.
 *
 *   - `src`     — the full URL with the current media token appended as
 *                 `?token=...`, or `null` while the first token is loading.
 *   - `onError` — wire this to the `<img onError={...}>` prop.
 *                 The handler attempts *one* token refresh; if the image
 *                 still fails after the fresh token, it surfaces the broken
 *                 state via `failed === true` and stops.
 *   - `failed`  — render `<ImageOff>` or "Image unavailable" when `true`.
 */
export interface ImageTokenState {
  src: string | null;
  onError: (event: SyntheticEvent<HTMLImageElement>) => void;
  failed: boolean;
}

/**
 * Derive a media-token–bearing URL and an `onError` retry handler for a
 * single `<img>` element that requires a short-lived Hub media token.
 *
 * ### Retry mechanism
 *
 * 1. On the first `onError` (`retried.current === false`):
 *    - Set `retried.current = true` to consume the one-retry budget.
 *    - Call `queryClient.invalidateQueries` on the message's token cache
 *      slot to force an immediate background refetch.
 *    - On the next render the token query resolves a fresh value, `src`
 *      rebuilds, and the browser re-fires the `<img>` load automatically
 *      because the `src` prop reference changed.
 * 2. On any subsequent `onError` (`retried.current === true`):
 *    - Set `failed` state to `true`.
 *    - The consumer renders a broken-image placeholder ("Image unavailable"
 *      / `ImageOff` icon) and stops retrying.
 *
 * The `retried` ref is a plain `useRef` (not state) — it must survive
 * re-renders without triggering one, and it must reset if the component
 * unmounts and remounts (blob → server URL transition). Because `useRef`
 * is initialised fresh on each mount, this is automatic.
 *
 * ### Usage
 *
 * ```tsx
 * const { src, onError, failed } = useImageWithMediaToken(message.id, media.url);
 * if (failed) return <ImageUnavailablePlaceholder />;
 * if (!src)   return <LoadingPlaceholder />;
 * return <img src={src} alt="…" onError={onError} />;
 * ```
 *
 * @param messageId - Used as the React Query cache key for the token; see
 *   `mediaTokenQueryKey`. Pass `message.id` so each message has its own
 *   independent retry window.
 * @param basePath  - The `/api/v1/media/{id}` path from the server (or a
 *   `blob:` URL for optimistic outbound messages). When `basePath` starts
 *   with `blob:` no token is appended and no token query is issued.
 */
export function useImageWithMediaToken(
  messageId: string,
  basePath: string,
): ImageTokenState {
  const isLocalBlob = basePath.startsWith('blob:');

  // One-shot retry budget: once flipped to true, further onError calls mean
  // the fresh token also failed — surface the broken placeholder and stop.
  const retried = useRef(false);
  const [failed, setFailed] = useState(false);

  const queryClient = useQueryClient();
  const tokenQuery = useMediaToken(messageId);

  const src = useMemo<string | null>(() => {
    if (isLocalBlob) return basePath;
    if (!tokenQuery.data) return null;
    return `${basePath}?token=${encodeURIComponent(tokenQuery.data.media_token)}`;
  }, [isLocalBlob, basePath, tokenQuery.data]);

  const onError = useCallback(() => {
    if (isLocalBlob) return; // blob: URLs don't use a token — nothing to retry.

    if (!retried.current) {
      // First failure: consume the retry budget and force a fresh token.
      retried.current = true;
      void queryClient.invalidateQueries({ queryKey: mediaTokenQueryKey(messageId) });
      // The query invalidation triggers a background refetch. When the token
      // resolves, `src` will recompute to a new string value which React will
      // push as a new `src` attribute — the browser will re-fire the request
      // automatically without any imperative DOM mutation needed.
    } else {
      // Second failure: the fresh token also didn't work. Show the broken
      // placeholder and stop retrying.
      setFailed(true);
    }
  }, [isLocalBlob, messageId, queryClient]);

  return { src, onError, failed };
}
